/**
 * Регистрация service worker'а и обнаружение обновления (CLIENT_WEB.md §9.5).
 *
 * §9.5 п. 2: `skipWaiting()` автоматически ЗАПРЕЩЁН — новая версия ждёт
 * явного действия пользователя (SYS-04). Здесь только обнаружение и
 * применение по команде; сам SW не активируется сам собой.
 *
 * В dev service worker не регистрируется: `devOptions.enabled = false`
 * в конфигурации vite-plugin-pwa, файла `/sw.js` на dev-сервере нет.
 */

/** Путь собранного worker'а: `injectManifest` с `filename: 'sw.ts'` → `/sw.js`. */
const SW_URL = '/sw.js'

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null
let reloading = false

function supported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
}

/**
 * Регистрирует worker один раз за жизнь вкладки и возвращает регистрацию.
 * Возвращает `null`, если worker недоступен (dev, отключён политикой, ошибка).
 */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registrationPromise) return registrationPromise

  if (!supported() || !import.meta.env.PROD) {
    registrationPromise = Promise.resolve(null)
    return registrationPromise
  }

  registrationPromise = navigator.serviceWorker
    .register(SW_URL, { type: 'classic', scope: '/' })
    .catch((error: unknown) => {
      console.warn('service worker registration failed', error)
      return null
    })

  return registrationPromise
}

/** Уже полученная регистрация без повторной попытки — для push-подписки. */
export async function currentRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!supported()) return null
  if (registrationPromise) return registrationPromise
  return (await navigator.serviceWorker.getRegistration()) ?? null
}

/**
 * SYS-04: появление новой версии. Колбэк вызывается, когда обновлённый worker
 * дождался активации (`waiting`) — приложение показывает предложение обновиться.
 */
export function onUpdateAvailable(callback: () => void): () => void {
  let cancelled = false

  void (async () => {
    const registration = await registerServiceWorker()
    if (!registration || cancelled) return

    if (registration.waiting) callback()

    registration.addEventListener('updatefound', () => {
      const installing = registration.installing
      if (!installing) return

      installing.addEventListener('statechange', () => {
        // `controller` есть только если страницей уже управляет старая версия:
        // первая установка обновлением не является.
        if (installing.state === 'installed' && navigator.serviceWorker.controller) callback()
      })
    })
  })()

  return () => {
    cancelled = true
  }
}

/**
 * §9.5 п. 3: применение обновления по действию пользователя —
 * `SKIP_WAITING` и перезагрузка после смены контроллера.
 */
export async function applyUpdate(): Promise<void> {
  const registration = await currentRegistration()
  const waiting = registration?.waiting
  if (!waiting) {
    location.reload()
    return
  }

  navigator.serviceWorker.addEventListener(
    'controllerchange',
    () => {
      if (reloading) return
      reloading = true
      location.reload()
    },
    { once: true },
  )

  waiting.postMessage({ type: 'SKIP_WAITING' })
}

/** Периодическая проверка обновления (§9.5 п. 4) — раз в интервал и при возврате вкладки. */
export function watchForUpdates(intervalMs = 60 * 60 * 1000): () => void {
  const check = (): void => {
    void currentRegistration().then((registration) => registration?.update())
  }

  const timer = setInterval(check, intervalMs)
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') check()
  }

  document.addEventListener('visibilitychange', onVisible)

  return () => {
    clearInterval(timer)
    document.removeEventListener('visibilitychange', onVisible)
  }
}
